/**
 * W1-TODAY-LAYOUT-MEMORY (founder ruling 2026-10-18, option A) - the block set the last
 * visit to Today drew, kept on this device so the 2.0 loading frame can draw it from the
 * first paint.
 *
 * WHY. The frame cannot know, before their reads answer, whether Today will draw a Next
 * steps card (CD 1c: only once the race read has settled empty) or a rest day's short hero
 * (CD 1b), so a frame that waited for those facts inserted a 346 px card mid-load and
 * shrank the hero 117 px at the swap: the week card moved twice in a quarter of a second
 * (UX pixel check 2026-10-18, defect 1). The device's own last visit is the best guess it
 * has before the reads answer, and a repeat visit (the common case) is then still.
 *
 * WHAT. Only what decides the geometry above the week card: the hero's day kind, the Next
 * steps rows, the coach line's word widths, the weather row's place, the Add location
 * prompt's place and the Apple Health row. Counts, flags and em widths: no sentence, no
 * name, no number of the athlete's body.
 * The coach line is stored as the WIDTHS of its words (the skeleton draws bars), never as
 * its text. The one identifier is the user id the record belongs to.
 *
 * WHERE. One localStorage slot (`tayro.todayLayout.v1`), stamped with the user id it
 * belongs to. One slot, not one key per user: the user id is not known at the first paint
 * (auth resolves after it), so the frame has to read "the last visit on this device" and
 * check the stamp once the id arrives; a second account that signs in simply replaces the
 * first one's record, so nothing of the first outlives the second's first visit. A record
 * for another user reads as no memory.
 *
 * PER ACCOUNT, GONE ON SIGN-OUT (Claude Design condition 1, 2026-10-18). The record is stamped
 * with its account, and signing out (`contexts/AuthContext`, the SIGNED_OUT branch) and deleting
 * the account (`lib/deviceState`) both clear it (`clearTodayLayout`), so a shared phone or the
 * dual athlete and coach account never draws another person's page.
 *
 * SAVED ONLY FROM A PAGE THAT HAS LANDED (condition 2): the writer in `routes/today.tsx` offers a
 * layout only once the 2.0 page itself is on screen and every read that decides its blocks has
 * settled, never from the loading frame or a partial render.
 *
 * FAIL-SOFT. Every read and write is in a try and catch (private modes and blocked storage
 * throw), a record that is not exactly the shape written here reads as no memory, and the
 * page works the same without it. The frame is never wrong in a way that breaks the page,
 * only plainer.
 */

/** The device's local date, YYYY-MM-DD. This module imports nothing: the auth provider calls its clearer. */
function localDay(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

export const TODAY_LAYOUT_KEY = 'tayro.todayLayout.v1';

/** The hero's day: a training day, a rest day (with or without a Tomorrow row), a done day. */
export type LayoutHero =
  | { kind: 'train' }
  | { kind: 'rest'; tomorrow: boolean }
  | { kind: 'done'; lines: number; effort: 'add' | 'edit' };
export type LayoutStep = 'approve' | 'draft' | 'add';
export interface LayoutCoachLine {
  kind: 'echo' | 'related';
  /** Each word's width in em, in order (the echo's first two are the coach's name and the dot). */
  words: readonly number[];
}
export type LayoutHealthRow = { kind: 'connect' } | { kind: 'stale'; day: string };

/** The block set that decides the geometry above the week card. Field names are the skeleton's props. */
export interface TodayLayout {
  hero: LayoutHero;
  nextSteps: LayoutStep[];
  /** The Draft step's weeks ("weeks 7 to 10"), drawn as bars only. */
  draftWeeksLabel: string | null;
  coachLine: LayoutCoachLine | null;
  /** The weather row's place (and the Apple Weather mark's). */
  weatherRow: boolean;
  /**
   * The Add location prompt (Ask 3) holds the weather slot's place: the athlete has no stored
   * location and has not pressed Not now (the page's `promptForLocation`). The hero draws it
   * unless the day is a rest day or a weather row is there, so the frame does the same. A
   * record written before this field existed reads as false.
   */
  weatherPrompt: boolean;
  healthRow: LayoutHealthRow | null;
  /**
   * The hero's action area is Start run, its helper line and the quiet Mark done (the native app
   * with `run_capture_v1` on) rather than the one pill. The frame cannot read the flag: the flags
   * are not in the page until the config read answers, which is mid-load.
   */
  startRun: boolean;
}

interface StoredRecord {
  v: 1;
  /** The user this layout is for. */
  user: string;
  /** The device's local date the hero kind applies to (YYYY-MM-DD). */
  day: string;
  layout: TodayLayout;
}

const ORDINARY_HERO: LayoutHero = { kind: 'train' };
const STEPS: readonly LayoutStep[] = ['approve', 'draft', 'add'];
const MAX_WORDS = 200;
const MAX_WORD_EM = 60;
const MAX_LABEL = 60;
const MAX_USER_ID = 128;

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null;
const isLabel = (x: unknown): x is string => typeof x === 'string' && x.length <= MAX_LABEL;

function parseHero(x: unknown): LayoutHero | null {
  if (!isObject(x)) return null;
  if (x.kind === 'train') return { kind: 'train' };
  if (x.kind === 'rest') return typeof x.tomorrow === 'boolean' ? { kind: 'rest', tomorrow: x.tomorrow } : null;
  if (x.kind === 'done') {
    const { lines, effort } = x;
    if ((lines === 0 || lines === 1 || lines === 2) && (effort === 'add' || effort === 'edit')) {
      return { kind: 'done', lines, effort };
    }
  }
  return null;
}

function parseSteps(x: unknown): LayoutStep[] | null {
  if (!Array.isArray(x) || x.length > STEPS.length) return null;
  const out: LayoutStep[] = [];
  for (const step of x) {
    if (!STEPS.includes(step as LayoutStep) || out.includes(step as LayoutStep)) return null;
    out.push(step as LayoutStep);
  }
  return out;
}

function parseCoachLine(x: unknown): LayoutCoachLine | null | undefined {
  if (x === null) return null;
  if (!isObject(x) || (x.kind !== 'echo' && x.kind !== 'related') || !Array.isArray(x.words)) return undefined;
  if (x.words.length === 0 || x.words.length > MAX_WORDS) return undefined;
  for (const w of x.words) {
    if (typeof w !== 'number' || !Number.isFinite(w) || w <= 0 || w > MAX_WORD_EM) return undefined;
  }
  return { kind: x.kind, words: x.words as number[] };
}

function parseHealthRow(x: unknown): LayoutHealthRow | null | undefined {
  if (x === null) return null;
  if (!isObject(x)) return undefined;
  if (x.kind === 'connect') return { kind: 'connect' };
  if (x.kind === 'stale' && isLabel(x.day)) return { kind: 'stale', day: x.day };
  return undefined;
}

function parseLayout(x: unknown): TodayLayout | null {
  if (!isObject(x)) return null;
  const hero = parseHero(x.hero);
  const nextSteps = parseSteps(x.nextSteps);
  const coachLine = parseCoachLine(x.coachLine);
  const healthRow = parseHealthRow(x.healthRow);
  const draftWeeksLabel = x.draftWeeksLabel === null ? null : isLabel(x.draftWeeksLabel) ? x.draftWeeksLabel : undefined;
  if (!hero || !nextSteps || coachLine === undefined || healthRow === undefined || draftWeeksLabel === undefined) {
    return null;
  }
  if (typeof x.weatherRow !== 'boolean' || typeof x.startRun !== 'boolean') return null;
  // Absent is a record from before the prompt was remembered: no prompt. Present and not a boolean is untrusted.
  if (x.weatherPrompt !== undefined && typeof x.weatherPrompt !== 'boolean') return null;
  return {
    hero,
    nextSteps,
    draftWeeksLabel,
    coachLine,
    weatherRow: x.weatherRow,
    weatherPrompt: x.weatherPrompt === true,
    healthRow,
    startRun: x.startRun,
  };
}

function parseRecord(raw: string | null): StoredRecord | null {
  if (!raw) return null;
  try {
    const x: unknown = JSON.parse(raw);
    if (!isObject(x) || x.v !== 1) return null;
    if (typeof x.user !== 'string' || x.user === '' || x.user.length > MAX_USER_ID) return null;
    if (typeof x.day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(x.day)) return null;
    const layout = parseLayout(x.layout);
    return layout ? { v: 1, user: x.user, day: x.day, layout } : null;
  } catch {
    return null;
  }
}

/** The widths are stored to 0.001em (the skeleton's own tables are measured to that). */
function rounded(layout: TodayLayout): TodayLayout {
  return layout.coachLine
    ? { ...layout, coachLine: { ...layout.coachLine, words: layout.coachLine.words.map((w) => Math.round(w * 1000) / 1000) } }
    : layout;
}

/**
 * Remember the block set the loaded 2.0 page just drew, for this user, on the device's
 * local date `now`. A layout that is already what is stored (same user, same day) is not
 * written again. Never throws: blocked storage is a memory that is not kept.
 */
export function rememberTodayLayout(userId: string, layout: TodayLayout, now: Date = new Date()): void {
  if (!userId || userId.length > MAX_USER_ID) return;
  try {
    const record: StoredRecord = { v: 1, user: userId, day: localDay(now), layout: rounded(layout) };
    const raw = JSON.stringify(record);
    if (window.localStorage.getItem(TODAY_LAYOUT_KEY) === raw) return;
    window.localStorage.setItem(TODAY_LAYOUT_KEY, raw);
  } catch {
    /* best-effort: the frame is plainer without a memory, the page is the same */
  }
}

/**
 * The block set the loading frame draws: what the last visit drew, or null when there is
 * no usable memory (the plain frame).
 *
 *   - `userId` null is "not known yet" (auth resolves after the first paint): the slot is
 *     taken as this device's last visit. Once the id is known a record stamped for
 *     another user reads as no memory.
 *   - A record from today is returned as it was written.
 *   - A record from an earlier day keeps its Next steps, coach line, weather, Add location
 *     prompt and Apple Health choices and uses the ordinary hero: the day's own kind (rest, done) says
 *     nothing about another day.
 */
export function recallTodayLayout(userId: string | null, now: Date = new Date()): TodayLayout | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(TODAY_LAYOUT_KEY);
  } catch {
    return null;
  }
  const record = parseRecord(raw);
  if (!record) return null;
  if (userId !== null && record.user !== userId) return null;
  return record.day === localDay(now) ? record.layout : { ...record.layout, hero: ORDINARY_HERO };
}

/**
 * Forget the layout: sign-out (`contexts/AuthContext`, the SIGNED_OUT branch) and account
 * deletion (`lib/deviceState`'s `clearOnAccountDeletion`) both call it, so a shared phone or the
 * dual athlete and coach account never draws another person's page (Claude Design condition 1,
 * 2026-10-18). Never throws.
 */
export function clearTodayLayout(): void {
  try {
    window.localStorage.removeItem(TODAY_LAYOUT_KEY);
  } catch {
    /* best-effort: blocked storage holds no memory to leave behind */
  }
}
